import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import UserListPage from './pages/users/UserListPage';
import UserDetailPage from './pages/users/UserDetailPage';
import PhoneReportListPage from './pages/reports/PhoneReportListPage';
import VictimReportListPage from './pages/reports/VictimReportListPage';
import BlacklistPage from './pages/security/BlacklistPage';
import WhitelistPage from './pages/security/WhitelistPage';
import ApkAnalysisPage from './pages/security/ApkAnalysisPage';
import DeepfakeAnalysisPage from './pages/security/DeepfakeAnalysisPage';
import VirusScanListPage from './pages/security/VirusScanListPage';
import UserScanHistoryPage from './pages/security/UserScanHistoryPage';
import SpamPhonePage from './pages/security/SpamPhonePage';
import FaqPage from './pages/content/FaqPage';
import NoticePage from './pages/content/NoticePage';
import ContactPage from './pages/content/ContactPage';
import NewsPage from './pages/content/NewsPage';
import PushNotificationPage from './pages/notifications/PushNotificationPage';
import WeeklyReportPage from './pages/reports/WeeklyReportPage';
import ChurnAnalysisPage from './pages/reports/ChurnAnalysisPage';
import AppLayout from './components/layout/AppLayout';
import PrivateRoute from './components/auth/PrivateRoute';
import ErrorBoundary from './components/common/ErrorBoundary';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

// Placeholder component for pages not yet implemented
const PlaceholderPage: React.FC<{ title: string }> = ({ title }) => (
  <div style={{ padding: '20px' }}>
    <h1>{title}</h1>
    <p>이 페이지는 아직 구현되지 않았습니다.</p>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <AppLayout>
              <Routes>
                {/* Dashboard */}
                <Route path="/dashboard" element={<DashboardPage />} />
                
                {/* User Management */}
                <Route path="/users" element={<UserListPage />} />
                <Route path="/users/:id" element={<UserDetailPage />} />
                
                {/* Report Management */}
                <Route path="/reports" element={<Navigate to="/reports/phone" />} />
                <Route path="/reports/phone" element={<PhoneReportListPage />} />
                <Route path="/reports/victim" element={<VictimReportListPage />} />
                <Route path="/reports/weekly" element={<WeeklyReportPage />} />
                <Route path="/reports/churn" element={<ChurnAnalysisPage />} />
                
                {/* Security Management */}
                <Route path="/security" element={<Navigate to="/security/blacklist" />} />
                <Route path="/security/blacklist" element={<BlacklistPage />} />
                <Route path="/security/whitelist" element={<WhitelistPage />} />
                <Route path="/security/virus-scan" element={<VirusScanListPage />} />
                <Route path="/security/user-scan-history" element={<UserScanHistoryPage />} />
                <Route path="/security/spam-phones" element={<SpamPhonePage />} />
                <Route path="/security/apk-analysis" element={<ApkAnalysisPage />} />
                <Route path="/security/deepfake-analysis" element={<DeepfakeAnalysisPage />} />
                
                {/* Content Management */}
                <Route path="/content" element={<Navigate to="/content/faq" />} />
                <Route path="/content/faq" element={<FaqPage />} />
                <Route path="/content/notice" element={<NoticePage />} />
                <Route path="/content/news" element={<NewsPage />} />
                <Route path="/content/contacts" element={<ContactPage />} />
                
                {/* Push Notifications */}
                <Route path="/notifications" element={<PushNotificationPage />} />
                
                {/* Default redirect */}
                <Route path="/" element={<Navigate to="/dashboard" />} />
                <Route path="*" element={<Navigate to="/dashboard" />} />
              </Routes>
            </AppLayout>
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <BrowserRouter>
            <AuthProvider>
              <AppRoutes />
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
